import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api, { STAGE_LABELS } from "@/lib/api";
import {
  X, History, ArrowRight, User as UserIcon, Clock, Globe, Play, Square, Pencil,
  CheckCircle2, AlertCircle, RefreshCw
} from "lucide-react";

/**
 * Read-only history modal with two tabs:
 *  - LAND DRAFTERS (default): stage transitions logged inside Land Drafters
 *  - SURVEYDRAFTS (only for jobs with `source === "surveydrafts"`): full
 *    status_history pulled live from surveydrafts.us via the cached
 *    `/jobs/{id}/surveydrafts-timeline` proxy.
 *
 * If `job` is omitted, the LAND DRAFTERS tab shows the org-wide recent 200.
 */
export default function StageAuditModal({ job, onClose }) {
  const hasSd = !!(job && job.source === "surveydrafts" && job.surveydrafts_id);
  const [tab, setTab] = useState("ld");      // "ld" | "sd"

  return createPortal(
    <div
      className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-3"
      onClick={onClose}
      data-testid="audit-log-overlay"
    >
      <div
        className="brut-card max-w-3xl w-full flex flex-col"
        style={{ maxHeight: "calc(100vh - 24px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-zinc-900 shrink-0 bg-white">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="label-mono text-zinc-500 flex items-center gap-1.5">
                <History className="w-3 h-3" /> AUDIT LOG
              </div>
              <h2 className="font-heading text-xl font-black">
                {job ? `Stage history · ${job.job_number}` : "Recent stage transitions"}
              </h2>
              {job && (
                <p className="text-xs font-mono text-zinc-600 mt-0.5 truncate">
                  {job.project_name || ""}
                </p>
              )}
            </div>
            <button onClick={onClose} className="brut-btn-secondary p-1.5" data-testid="audit-log-close">
              <X className="w-4 h-4" />
            </button>
          </div>
          {hasSd && (
            <div className="flex gap-1" data-testid="audit-tabs">
              <TabBtn active={tab === "ld"} onClick={() => setTab("ld")} icon={History} label="LAND DRAFTERS" testid="audit-tab-ld" />
              <TabBtn active={tab === "sd"} onClick={() => setTab("sd")} icon={Globe} label="SURVEYDRAFTS.US" testid="audit-tab-sd" />
            </div>
          )}
        </div>

        <div className="overflow-y-auto flex-1 min-h-0">
          {tab === "ld" && <LDPanel job={job} />}
          {tab === "sd" && <SDPanel job={job} />}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 text-xs font-mono font-bold border border-zinc-900 flex items-center gap-1.5 ${
        active ? "bg-yellow-200" : "bg-white hover:bg-zinc-50"
      }`}
    >
      <Icon className="w-3 h-3" /> {label}
    </button>
  );
}

// =============================================================================
// LAND DRAFTERS tab — existing stage transition log
// =============================================================================
function LDPanel({ job }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    const params = job ? { job_id: job.id } : { limit: 200 };
    api.get("/jobs/audit-log", { params })
      .then(({ data }) => setRows(data || []))
      .catch(() => { setErr("Could not load audit log"); setRows([]); });
  }, [job]);

  const fmtTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso.slice(0, 16); }
  };

  const sourceBadge = (src) => {
    const map = {
      manual: { bg: "bg-zinc-100", label: "MANUAL" },
      bulk: { bg: "bg-blue-100", label: "BULK" },
      work_session: { bg: "bg-yellow-100", label: "WORK SESSION" },
      pick_up: { bg: "bg-green-100", label: "PICK-UP" },
    }[src] || { bg: "bg-zinc-100", label: (src || "OTHER").toUpperCase() };
    return <span className={`label-mono px-1.5 py-0.5 border border-zinc-900 ${map.bg}`}>{map.label}</span>;
  };

  if (rows === null) return <div className="px-5 py-10 text-center text-zinc-500 font-mono text-xs">LOADING…</div>;
  if (err) return <div className="px-5 py-10 text-center text-red-700 font-mono text-xs">{err}</div>;
  if (rows.length === 0) return (
    <div className="px-5 py-10 text-center text-zinc-500 font-mono text-xs">
      NO TRANSITIONS RECORDED YET. AS SOON AS A CARD MOVES BETWEEN COLUMNS WE&apos;LL LOG IT HERE.
    </div>
  );

  return (
    <ul className="divide-y divide-zinc-200">
      {rows.map((r) => (
        <li key={r.id} className="px-5 py-3 hover:bg-zinc-50 flex items-start gap-3" data-testid={`audit-row-${r.id}`}>
          <div className="shrink-0 mt-0.5 text-zinc-500"><Clock className="w-3.5 h-3.5" /></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap text-sm">
              <span className="font-mono text-xs bg-zinc-100 border border-zinc-300 px-1.5 py-0.5">
                {STAGE_LABELS[r.from_stage] || r.from_stage || "—"}
              </span>
              <ArrowRight className="w-3 h-3 text-zinc-500" />
              <span className="font-mono text-xs font-bold bg-yellow-100 border border-zinc-900 px-1.5 py-0.5">
                {STAGE_LABELS[r.to_stage] || r.to_stage}
              </span>
              {sourceBadge(r.source)}
              {!job && r.job_number && (
                <span className="font-mono text-[10px] text-zinc-500 truncate" title={r.job_number}>
                  · {r.job_number}
                </span>
              )}
            </div>
            <div className="text-xs font-mono text-zinc-600 mt-1 flex items-center gap-2 flex-wrap">
              <span className="flex items-center gap-1"><UserIcon className="w-3 h-3" /> {r.user_name || r.user_email || "unknown"}</span>
              <span>·</span>
              <span>{fmtTime(r.at)}</span>
              {r.extra?.next_assignee && (
                <><span>·</span><span>handed to <b>{r.extra.next_assignee}</b></span></>
              )}
              {typeof r.extra?.taken_hours === "number" && r.extra.taken_hours > 0 && (
                <><span>·</span><span>logged <b>{r.extra.taken_hours}h</b></span></>
              )}
              {r.extra?.batch_size && (
                <><span>·</span><span>part of bulk advance ({r.extra.batch_size} jobs)</span></>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

// =============================================================================
// SURVEYDRAFTS.US tab — full assignment cycle pulled live (cached 5 min)
// =============================================================================
function SDPanel({ job }) {
  const [state, setState] = useState({ loading: true, ok: false, history: [], cached: false, age_min: null, reason: "" });

  const load = (refresh = false) => {
    setState((s) => ({ ...s, loading: true }));
    api.get(`/jobs/${job.id}/surveydrafts-timeline`, { params: refresh ? { refresh: true } : {} })
      .then(({ data }) => {
        setState({
          loading: false,
          ok: !!data.ok,
          history: data.history || [],
          cached: !!data.cached,
          age_min: data.age_min ?? null,
          reason: data.reason || "",
        });
      })
      .catch(() => setState({ loading: false, ok: false, history: [], cached: false, age_min: null, reason: "request failed" }));
  };
  useEffect(() => { load(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [job?.id]);

  const fmtTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch { return String(iso).slice(0, 16); }
  };

  if (state.loading) return <div className="px-5 py-10 text-center text-zinc-500 font-mono text-xs">LOADING SURVEYDRAFTS TIMELINE…</div>;
  if (!state.ok) {
    return (
      <div className="px-5 py-10 text-center font-mono text-xs">
        <AlertCircle className="w-5 h-5 mx-auto text-amber-600 mb-2" />
        <div className="text-zinc-700">Could not load timeline from surveydrafts.us</div>
        {state.reason && <div className="text-zinc-500 mt-1">{state.reason}</div>}
        <button onClick={() => load(true)} className="brut-btn-secondary mt-3 px-2 py-1 text-[10px] inline-flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> RETRY
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between px-5 py-2 border-b border-zinc-200 bg-zinc-50">
        <div className="text-[10px] font-mono text-zinc-500">
          {state.cached
            ? <>CACHED · age <b>{state.age_min}m</b> · refresh-TTL 5m</>
            : <>FETCHED LIVE FROM SURVEYDRAFTS.US</>}
          · {state.history.length} events
        </div>
        <button onClick={() => load(true)} className="brut-btn-secondary px-2 py-1 text-[10px] inline-flex items-center gap-1" data-testid="sd-timeline-refresh">
          <RefreshCw className="w-3 h-3" /> REFRESH
        </button>
      </div>
      {state.history.length === 0 ? (
        <div className="px-5 py-10 text-center text-zinc-500 font-mono text-xs">
          NO EVENTS RECORDED FOR THIS JOB ON SURVEYDRAFTS.US YET.
        </div>
      ) : (
        <ul className="divide-y divide-zinc-200">
          {state.history.map((e, i) => (
            <li key={e.id || i} className="px-5 py-3 hover:bg-zinc-50 flex items-start gap-3" data-testid={`sd-event-${e.id || i}`}>
              <ActionIcon type={e.action_type} />
              <div className="flex-1 min-w-0">
                <div className="text-sm">
                  <ActionBadge type={e.action_type} />
                  <span className="ml-2">{e.description || "—"}</span>
                </div>
                <div className="text-xs font-mono text-zinc-600 mt-1 flex items-center gap-2 flex-wrap">
                  {e.performed_by_name && (
                    <span className="flex items-center gap-1">
                      <UserIcon className="w-3 h-3" /> {e.performed_by_name}
                    </span>
                  )}
                  <span>·</span>
                  <span>{fmtTime(e.created_at)}</span>
                  {typeof e.details?.duration_hours === "number" && (
                    <><span>·</span><span><b>{e.details.duration_hours}h</b> session</span></>
                  )}
                  {e.details?.from_status && e.details?.to_status && (
                    <>
                      <span>·</span>
                      <span className="font-bold">{e.details.from_status}</span>
                      <ArrowRight className="w-3 h-3 text-zinc-500" />
                      <span className="font-bold">{e.details.to_status}</span>
                    </>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const ACTION_META = {
  timer_start:     { icon: Play,         tone: "bg-emerald-100", label: "TIMER START" },
  timer_stop:      { icon: Square,       tone: "bg-rose-100",    label: "TIMER STOP" },
  status_change:   { icon: ArrowRight,   tone: "bg-yellow-100",  label: "STATUS" },
  assignment:      { icon: UserIcon,     tone: "bg-indigo-100",  label: "ASSIGNED" },
  assignment_change:{ icon: UserIcon,    tone: "bg-indigo-100",  label: "REASSIGNED" },
  note_added:     { icon: Pencil,        tone: "bg-zinc-100",    label: "NOTE" },
  completed:       { icon: CheckCircle2, tone: "bg-emerald-200", label: "COMPLETED" },
};

function ActionIcon({ type }) {
  const m = ACTION_META[type] || { icon: Clock, tone: "bg-zinc-100" };
  const Icon = m.icon;
  return (
    <span className={`w-6 h-6 border border-zinc-900 ${m.tone} flex items-center justify-center shrink-0 mt-0.5`}>
      <Icon className="w-3 h-3" />
    </span>
  );
}

function ActionBadge({ type }) {
  const m = ACTION_META[type] || { tone: "bg-zinc-100", label: (type || "EVENT").toUpperCase().replace(/_/g, " ") };
  return <span className={`label-mono px-1.5 py-0.5 border border-zinc-900 ${m.tone}`}>{m.label}</span>;
}
